import React, { RefObject, useCallback, useContext, useEffect, useRef } from 'react';

/** CSS custom property on `<html>` holding how many px of the viewport bottom
 *  the bottom bars (fixed, or sticky and pinned) cover. Anything that must float above them (a banner,
 *  a toast) positions against `var(--belhop-bottom-chrome-inset, 0px)`. */
export const BOTTOM_CHROME_INSET_VARIABLE = '--belhop-bottom-chrome-inset';

type RegisterBottomChrome = (element: HTMLElement) => () => void;

const BottomChromeContext = React.createContext<RegisterBottomChrome | null>(null);

/** How far below the stack built so far a bar's bottom edge may sit and still
 *  count as touching it. The booking bars are offset by the tab bar's nominal
 *  height while the tab bar renders a few px taller (its padding), so a pinned
 *  booking bar overlaps the tab bar slightly rather than meeting it exactly. */
const STACK_CONTACT_TOLERANCE_PX = 2;

/**
 * Height of the stack of bars touching the viewport bottom. A sticky booking
 * bar that has docked above the footer and scrolled up no longer covers
 * anything at the bottom, so it only counts while it is contiguous with the
 * bars below it.
 */
function measureInsetPx(elements: Iterable<HTMLElement>): number {
  const rects = Array.from(elements, (element) => element.getBoundingClientRect()).sort(
    (first, second) => second.bottom - first.bottom,
  );
  let inset = 0;
  for (const rect of rects) {
    if (rect.bottom >= window.innerHeight - inset - STACK_CONTACT_TOLERANCE_PX) {
      inset = Math.max(inset, window.innerHeight - rect.top);
    }
  }
  return inset;
}

/**
 * Tracks the bottom bars (fixed, or sticky and pinned) that register through `useRegisterBottomChrome`
 * and publishes their combined height as `--belhop-bottom-chrome-inset`.
 */
export const BottomChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const registeredElements = useRef(new Set<HTMLElement>());
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const publishInset = useCallback(() => {
    document.documentElement.style.setProperty(
      BOTTOM_CHROME_INSET_VARIABLE,
      `${measureInsetPx(registeredElements.current)}px`,
    );
  }, []);

  const register = useCallback<RegisterBottomChrome>(
    (element) => {
      registeredElements.current.add(element);
      resizeObserver.current?.observe(element);
      publishInset();
      return () => {
        registeredElements.current.delete(element);
        resizeObserver.current?.unobserve(element);
        publishInset();
      };
    },
    [publishInset],
  );

  useEffect(() => {
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver.current = new ResizeObserver(publishInset);
      registeredElements.current.forEach((element) => resizeObserver.current?.observe(element));
    }
    window.addEventListener('resize', publishInset);
    window.addEventListener('scroll', publishInset, { passive: true });
    publishInset();
    return () => {
      window.removeEventListener('resize', publishInset);
      window.removeEventListener('scroll', publishInset);
      resizeObserver.current?.disconnect();
      resizeObserver.current = null;
    };
  }, [publishInset]);

  return <BottomChromeContext.Provider value={register}>{children}</BottomChromeContext.Provider>;
};

/**
 * Registers the element behind `elementRef` as bottom chrome. A no-op
 * outside a provider and while the ref is empty (a bar that renders
 * conditionally registers once it mounts).
 */
export function useRegisterBottomChrome(elementRef: RefObject<HTMLElement | null>): void {
  const register = useContext(BottomChromeContext);
  const registeredElement = useRef<HTMLElement | null>(null);
  const unregister = useRef<(() => void) | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (register === null || element === registeredElement.current) return;
    unregister.current?.();
    registeredElement.current = element;
    unregister.current = element === null ? null : register(element);
  });

  useEffect(
    () => () => {
      unregister.current?.();
      unregister.current = null;
      registeredElement.current = null;
    },
    [],
  );
}
